import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  TextField,
  Button,
  Paper,
  Grid,
  Radio,
  FormControlLabel,
  Stack,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
} from "@mui/material";
import { DoubleArrow, MoreVert } from "@mui/icons-material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import ModeStandbyIcon from "@mui/icons-material/ModeStandby";
import CommentIcon from "@mui/icons-material/Comment";
import EditIcon from "@mui/icons-material/Edit";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ShoppingBagOutlinedIcon from "@mui/icons-material/ShoppingBagOutlined";
import CardGiftcardOutlinedIcon from "@mui/icons-material/CardGiftcardOutlined";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { customAPI, customGetAPI } from "services/utils/CustomAPI";
import { showToast } from "services/utils/Status";
import { LoaderWithoutHeight } from "components/Loader";
import { UserDetailsRow } from "components/CustomComponents/UserDetailsRow";
import { themes, infoStyle } from "services/constants";

// Helper to build timeline history robustly
export const buildStatusHistory = (data) => {
  if (!data) return [];
  const history = [];

  if (data.applied_at || data.request_date) {
    history.push({
      status: "Applied",
      user: data.applied_by || data.user_name || "User",
      date: data.applied_at || data.request_date,
      color: "#00ab66",
    });
  }

  if (data.status === "Rejected") {
    history.push({
      status: "Pre-Approval: Rejected",
      user: data.rejected_by || data.approver_name || "Admin",
      date: data.rejected_at || "N/A",
      color: "#ff0000",
    });
    if (data.rejection_reason) {
      history.push({
        status: "Reason",
        reason: data.rejection_reason,
        isReason: true,
        color: "#d3d3d3",
      });
    }
  }

  if (data.status === "Approved" || data.status === "Active") {
    history.push({
      status: data.status === "Active" ? "Activated" : "Approved",
      user: data.approved_by || data.approver_name || "Admin",
      date: data.approved_at || "N/A",
      color: "#00ab66",
    });
  }

  return history;
};

// Fixed Custom Timeline Component (No double dots, clean start/stop connector)
export const CustomTimeline = ({ history }) => (
  <Box sx={{ mt: 1, ml: 1 }}>
    {history.map((item, index) => {
      const isLast = index === history.length - 1;
      return (
        <Box
          key={index}
          sx={{ position: "relative", pl: 3, pb: isLast ? 0 : 3 }}
        >
          {/* Connector Line (Only render if it's NOT the last item) */}
          {!isLast && (
            <Box
              sx={{
                position: "absolute",
                left: "4px", // Centers exactly under the 10px dot
                top: "14px",
                bottom: "-2px",
                width: "2px",
                bgcolor: "#eee",
              }}
            />
          )}
          {/* Timeline Dot Badge */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              top: 5,
              width: 10, // Reduced badge size to match design
              height: 10,
              borderRadius: "50%",
              bgcolor: item.color || "#ccc",
              zIndex: 1,
            }}
          />
          {item.isReason ? (
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              <strong>{item.status}:</strong> {item.reason}
            </Typography>
          ) : (
            <Typography variant="body2">
              <strong>{item.status}</strong>{" "}
              {item.user ? `by ${item.user}` : ""} On {item.date}
            </Typography>
          )}
        </Box>
      );
    })}
  </Box>
);

const InfoCard = ({ label, value, icon: Icon, color }) => (
  <Box
    sx={{
      mb: 2,
      p: 2,
      borderRadius: 2,
      bgcolor: color,
      border: "1px solid #eee",
    }}
  >
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ mb: 1, opacity: 0.7 }}
    >
      {Icon && <Icon sx={{ fontSize: 18 }} />}
      <Typography variant="caption" fontWeight="bold">
        {label}
      </Typography>
    </Stack>
    <Typography variant="h6" sx={{ fontWeight: 700 }}>
      {value}
    </Typography>
  </Box>
);

const SummaryCard = ({ title, amount, icon: Icon, color, lightColor }) => (
  <Paper
    variant="outlined"
    sx={{ p: 2, borderRadius: 2, borderColor: color, flex: 1 }}
  >
    <Stack direction="row" spacing={1} alignItems="center" mb={1}>
      <Box
        sx={{
          p: 0.5,
          borderRadius: 1,
          bgcolor: lightColor,
          color: color,
          display: "flex",
        }}
      >
        <Icon fontSize="small" />
      </Box>
      <Typography variant="caption" color="text.secondary" fontWeight="bold">
        {title}
      </Typography>
    </Stack>
    <Typography variant="h5" fontWeight="bold">
      {amount}
    </Typography>
  </Paper>
);

const AdvanceSalaryPanel = ({
  onClose,
  reCallApi,
  mode = "create",
  initialData = null,
  onEdit,
}) => {
  const { domain, userDetails } = useSelector((state) => state.tokenReducer);

  const isView = mode === "view";
  const isEdit = mode === "edit";
  const isDetailedView =
    isView &&
    (initialData?.status === "Approved" || initialData?.status === "Active");

  const [detailedData, setDetailedData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  const [formData, setFormData] = useState({
    amount: "",
    reason: "",
    repayment_mode: "Full",
    repayment_date: dayjs()
      .add(1, "month")
      .startOf("month")
      .format("YYYY-MM-DD"),
    no_of_installments: 1,
  });

  // Fetch detailed data properly mapped
  useEffect(() => {
    if (isDetailedView && initialData?.id) {
      const fetchDetails = async () => {
        setLoadingDetails(true);
        try {
          const res = await customGetAPI(
            `${domain}advance/request/${initialData.id}/`,
          );
          // Extract the nested 'data' object reliably
          const extractedData = res.responseDetails || res.data || res;
          setDetailedData(extractedData);
        } catch (err) {
          console.error("Failed to fetch advance details", err);
        } finally {
          setLoadingDetails(false);
        }
      };
      fetchDetails();
    }
  }, [isDetailedView, initialData?.id, domain]);

  useEffect(() => {
    if ((isView || isEdit) && initialData) {
      const repaymentDate =
        initialData.repayment_date || initialData.repayment_start_date || "";
      // Ensure date is in YYYY-MM-DD format for date input
      const formattedDate = repaymentDate
        ? dayjs(repaymentDate, "DD-MM-YYYY").format("YYYY-MM-DD")
        : "";

      setFormData({
        amount: initialData.amount || "",
        reason: initialData.description || initialData.reason || "",
        repayment_mode: initialData.repayment_mode || "Full",
        effective_repayment_date: formattedDate,
        no_of_installments:
          initialData.no_of_installments ||
          initialData.number_of_installments ||
          1,
      });
    }
  }, [isView, isEdit, initialData]);

  // Handle Double nested 'data' scenarios if API response dictates
  const displayData = detailedData?.data || detailedData || initialData || {};
  const timelineHistory = buildStatusHistory(displayData);

  const isInstallment = formData.repayment_mode?.startsWith("Installment");
  const monthlyAmount =
    isInstallment && formData.amount && formData.no_of_installments > 0
      ? (
          parseFloat(formData.amount) / parseInt(formData.no_of_installments)
        ).toFixed(2)
      : 0;

  const handleChange = (field, value) => {
    // Validate date fields - prevent past dates
    if (field === "effective_repayment_date" && value) {
      const selectedDate = dayjs(value, "YYYY-MM-DD");
      const today = dayjs().startOf("day");
      if (selectedDate.isBefore(today)) {
        showToast("error", "Please select a future date for repayment.");
        return;
      }
    }

    setFormData((prev) => {
      const newState = { ...prev, [field]: value };
      if (field === "repayment_mode" && value === "Full") {
        newState.no_of_installments = 1;
      }
      return newState;
    });
  };

  const isSubmitDisabled = () => {
    if (!formData.amount || parseFloat(formData.amount) <= 0) return true;
    if (!formData.reason?.trim()) return true;
    if (!formData.effective_repayment_date) return true;
    if (
      isInstallment &&
      (!formData.no_of_installments || formData.no_of_installments < 1)
    )
      return true;
    return false;
  };

  const handleSubmit = async () => {
    setActionLoading(true);
    const payload = {
      amount: parseFloat(formData.amount),
      reason: formData.reason,
      repayment_mode: formData.repayment_mode,
      repayment_date: formData.effective_repayment_date,
      no_of_installments: isInstallment
        ? parseInt(formData.no_of_installments)
        : 1,
      user_id: userDetails?.id,
    };

    try {
      const endpoint = isEdit
        ? `${domain}advance/request/${initialData.id}/`
        : `${domain}advance/request/`;
      const method = isEdit ? "PUT" : "POST";
      const res = await customAPI(endpoint, method, payload);

      if (res.ok) {
        showToast(
          "success",
          `Advance request ${isEdit ? "updated" : "submitted"} successfully`,
        );
        if (reCallApi) reCallApi(true);
        if (onClose) onClose();
      } else {
        showToast("error", res.message || "Failed to submit request");
      }
    } catch (error) {
      console.error("Advance Submit Error:", error);
      showToast("error", "Something went wrong.");
    } finally {
      setActionLoading(false);
    }
  };

  const renderDetailedView = () => (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 2 }}>
        <Box
          sx={{
            border: "1px solid #00ab66",
            color: "#00ab66",
            borderRadius: 4,
            px: 2,
            py: 0.5,
            fontSize: "0.8rem",
            fontWeight: "bold",
          }}
        >
          {displayData.status === "Active" ? "Active" : "Partially Recovered"}
        </Box>
      </Box>

      <Stack direction="row" spacing={2} mb={3}>
        <SummaryCard
          title="Advance Amount"
          amount={displayData.approved_amount || displayData.amount || "-"}
          icon={ShoppingBagOutlinedIcon}
          color="#2ecc71"
          lightColor="#eafaf1"
        />
        <SummaryCard
          title="Paid Amount"
          amount={displayData.paid_amount || "0.00"}
          icon={AccountBalanceWalletOutlinedIcon}
          color="#e91e63"
          lightColor="#fce4ec"
        />
        <SummaryCard
          title="Balance"
          amount={displayData.balance_amount || "-"}
          icon={CardGiftcardOutlinedIcon}
          color="#3f51b5"
          lightColor="#e8eaf6"
        />
      </Stack>

      <Paper variant="outlined" sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={2}>
          {[
            { label: "Request date", value: displayData.request_date },
            { label: "Amount", value: displayData.effective_amount },
            { label: "Reason", value: displayData.reason },
            {
              label: "Disbursement date",
              value: displayData.disbursement_date || "-",
            },
            { label: "Repayment Mode", value: displayData.repayment_mode },
            {
              label: "No of Instalement",
              value:
                displayData.approved_installments ||
                displayData.no_of_installments,
            },
          ].map((item, i) => (
            <React.Fragment key={i}>
              <Grid item xs={5}>
                <Typography variant="subtitle2" fontWeight="bold">
                  {item.label}
                </Typography>
              </Grid>
              <Grid item xs={7}>
                <Typography variant="body2" color="text.secondary">
                  {item.value || "-"}
                </Typography>
              </Grid>
            </React.Fragment>
          ))}
        </Grid>
      </Paper>

      <Typography variant="subtitle1" fontWeight="bold" mb={2}>
        Repayment History
      </Typography>
      <TableContainer
        component={Paper}
        variant="outlined"
        sx={{ borderRadius: 2 }}
      >
        <Table size="small">
          <TableHead sx={{ bgcolor: "#f5f5f5" }}>
            <TableRow>
              <TableCell sx={{ fontWeight: "bold" }}>
                Repayment Due Date
              </TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Due</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Paid Amount</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Balance</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Payrun</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
              <TableCell sx={{ fontWeight: "bold" }}>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Maps successfully now since we properly extracted inner data */}
            {displayData.repayment_schedule?.length > 0 ? (
              displayData.repayment_schedule.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.due_date || "-"}</TableCell>
                  <TableCell>{row.amount_due || "-"}</TableCell>
                  <TableCell>{row.amount_paid || "-"}</TableCell>
                  <TableCell>{row.balance || "-"}</TableCell>
                  <TableCell>{row.payrun || "-"}</TableCell>
                  <TableCell>{row.status || "-"}</TableCell>
                  <TableCell>
                    <IconButton size="small">
                      <MoreVert fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 3 }}>
                  No repayment schedule available
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "#fff",
        width: "100%",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          borderBottom: `1px solid ${themes?.miliedLightGray}`,
          borderRadius: 0,
          pb: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", px: 2, pt: 2 }}>
          <IconButton
            onClick={onClose}
            sx={{ color: themes?.grayColor, mr: 1, ml: -1 }}
          >
            <DoubleArrow />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700 }}>
              {isView
                ? isDetailedView
                  ? "Advance"
                  : "View Advance Request"
                : isEdit
                  ? "Edit Advance Request"
                  : "Advance Request"}
            </Typography>
            <Typography {...infoStyle}>
              {isView
                ? "View your payoff request."
                : "Submit an advance request and choose a repayment option."}
            </Typography>
          </Box>
          {isView &&
            onEdit &&
            !isDetailedView &&
            initialData?.status !== "Rejected" && (
              <IconButton
                onClick={onEdit}
                size="small"
                sx={{ bgcolor: "#e8f5e9", color: "#00ab66" }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            )}
        </Box>
      </Paper>

      <Box sx={{ flex: 1, overflowY: "auto", p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <UserDetailsRow
            userDetails={{
              image_url: userDetails?.image_url,
              employee_id: displayData?.employee_id || userDetails?.employee_id,
              user_name: displayData?.user_name || userDetails?.first_name,
              department_name:
                displayData?.department || userDetails?.department_name,
              designation: displayData?.designation || userDetails?.designation,
            }}
          />
        </Box>

        {isView ? (
          loadingDetails ? (
            <LoaderWithoutHeight info="Loading details..." />
          ) : isDetailedView ? (
            renderDetailedView()
          ) : (
            <Box>
              <InfoCard
                label="Amount"
                value={formData.amount}
                icon={AccountBalanceWalletOutlinedIcon}
                color="#f0f7ff"
              />
              {/* Fix: Only displays the dates based on the mode correctly */}
              {formData.repayment_mode === "Full" ? (
                <InfoCard
                  label="Repayment Date"
                  value={dayjs(formData.effective_repayment_date).format(
                    "DD.MM.YYYY",
                  )}
                  icon={CalendarTodayIcon}
                  color="#d6e6e3"
                />
              ) : (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Box sx={{ flex: 1 }}>
                    <InfoCard
                      label="No. of Installments"
                      value={formData.no_of_installments}
                      icon={HelpOutlineIcon}
                      color="#fffcf0"
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <InfoCard
                      label="Monthly Amount"
                      value={monthlyAmount}
                      icon={AccountBalanceWalletIcon}
                      color="#fffcf0"
                    />
                  </Box>
                </Box>
              )}

              {/* always show mode */}
              <InfoCard
                label="Repayment Mode"
                value={formData.repayment_mode}
                icon={ModeStandbyIcon}
                color="#fff0f6"
              />

              {/* show start date separately when using installment */}
              {isInstallment && (
                <InfoCard
                  label="Repayment Start Date"
                  value={dayjs(formData.effective_repayment_date).format(
                    "DD.MM.YYYY",
                  )}
                  icon={CalendarTodayIcon}
                  color="#e2f3f0"
                />
              )}
              <InfoCard
                label="Reason"
                value={formData.reason}
                icon={CommentIcon}
                color="#e9e6f7"
              />

              {timelineHistory.length > 0 && (
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Typography
                    variant="subtitle2"
                    sx={{ mb: 1, fontWeight: "bold" }}
                  >
                    Status History
                  </Typography>
                  <CustomTimeline history={timelineHistory} />
                </Grid>
              )}
            </Box>
          )
        ) : (
          <Stack spacing={3}>
            {/* Same Create/Edit Fields as Before */}
            <Box>
              <Typography variant="subtitle2" fontWeight="bold">
                Request date
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                This date is used for approval and payroll processing.
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "#00ab66", fontWeight: "bold", mt: 0.5 }}
              >
                {dayjs().format("DD.MM.YYYY")}
              </Typography>
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Amount
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Enter the advance amount you wish to request.
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="number"
                value={formData.amount}
                onChange={(e) => handleChange("amount", e.target.value)}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Reason
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Mention the reason for requesting the advance. This will help in
                faster approval.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                size="small"
                value={formData.reason}
                onChange={(e) => handleChange("reason", e.target.value)}
              />
            </Box>

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Repayment mode
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Choose the repayment mode for the advance.
              </Typography>
              <Grid container spacing={2} sx={{ mt: 0.5 }}>
                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    onClick={() => handleChange("repayment_mode", "Full")}
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      borderRadius: 2,
                      border:
                        formData.repayment_mode === "Full"
                          ? "2px solid #00ab66"
                          : "1px solid #ddd",
                      bgcolor:
                        formData.repayment_mode === "Full"
                          ? "#f0fff4"
                          : "transparent",
                    }}
                  >
                    <FormControlLabel
                      value="Full"
                      control={
                        <Radio
                          checked={formData.repayment_mode === "Full"}
                          size="small"
                          sx={{ color: "#00ab66" }}
                        />
                      }
                      label={
                        <Typography variant="subtitle2" fontWeight="bold">
                          Full
                        </Typography>
                      }
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ ml: 3.5 }}
                    >
                      One-time deduction in the next applicable payroll after
                      approval.
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    variant="outlined"
                    onClick={() =>
                      handleChange("repayment_mode", "Installment")
                    }
                    sx={{
                      p: 1.5,
                      cursor: "pointer",
                      borderRadius: 2,
                      border:
                        formData.repayment_mode === "Installment"
                          ? "2px solid #00ab66"
                          : "1px solid #ddd",
                      bgcolor:
                        formData.repayment_mode === "Installment"
                          ? "#f0fff4"
                          : "transparent",
                    }}
                  >
                    <FormControlLabel
                      value="Installment"
                      control={
                        <Radio
                          checked={formData.repayment_mode === "Installment"}
                          size="small"
                          sx={{ color: "#00ab66" }}
                        />
                      }
                      label={
                        <Typography variant="subtitle2" fontWeight="bold">
                          Installment
                        </Typography>
                      }
                    />
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ ml: 3.5 }}
                    >
                      The advance amount will be split into instalments and
                      deducted monthly.
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {isInstallment && (
              <Box>
                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      No. of Installments
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ minHeight: 40, mb: 1 }}
                    >
                      Number of payroll cycles over which the advance will be
                      repaid.
                    </Typography>
                    <TextField
                      fullWidth
                      size="small"
                      type="number"
                      value={formData.no_of_installments}
                      onChange={(e) =>
                        handleChange("no_of_installments", e.target.value)
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Monthly Repayment Amount
                    </Typography>
                    <Typography
                      variant="caption"
                      display="block"
                      color="text.secondary"
                      sx={{ minHeight: 40, mb: 1 }}
                    >
                      Amount to be deducted from each payroll cycle.
                    </Typography>
                    <Box
                      sx={{
                        height: "40px",
                        display: "flex",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{ color: "#00ab66", fontWeight: "bold" }}
                      >
                        {monthlyAmount}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" fontWeight="bold" mb={0.5}>
                {isInstallment ? "Repayment Start Date" : "Repayment date"}
              </Typography>
              <TextField
                fullWidth
                size="small"
                type="date"
                value={formData.effective_repayment_date}
                onChange={(e) =>
                  handleChange("effective_repayment_date", e.target.value)
                }
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: dayjs().format("YYYY-MM-DD"),
                }}
              />
            </Box>
          </Stack>
        )}
      </Box>

      {!isView && (
        <Box sx={{ p: 2, borderTop: "1px solid #eee" }}>
          <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={isSubmitDisabled() || actionLoading}
              sx={{ bgcolor: "#84deb8", px: 4 }}
            >
              {actionLoading ? "Processing..." : isEdit ? "Update" : "Create"}
            </Button>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default AdvanceSalaryPanel;
